import { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useApi } from '../hooks/useApi';
import { fmtDate, isPast } from '../events/util';
import TabIngreso from '../events/tabs/Ingreso';
import TabCajas from '../events/tabs/Cajas';
import TabVentas from '../events/tabs/Ventas';
import TabAdministracion from '../events/tabs/Administracion';
import styles from './Panel.module.css';

// El panel del ADMINISTRADOR muestra, para el evento elegido, las mismas pestañas
// que ve el dueño en ese evento: Ingreso, Cajas, Entradas vendidas y Administración
// (las notas de la noche viven adentro de Administración, en "Conclusión de la
// noche"). Antes estas secciones existían pero mostraban todas el mismo cartel de
// "próximamente": la pantalla nunca se había construido.
const SECTIONS = [
  { key: 'ventas', label: 'ENTRADAS VENDIDAS' },
  { key: 'ingreso', label: 'INGRESO' },
  { key: 'cajas', label: 'CAJAS' },
  { key: 'administracion', label: 'ADMINISTRACIÓN' },
];

export default function AdminPanel() {
  const { user, logout } = useAuth();
  const api = useApi();
  const [section, setSection] = useState('ventas');
  const [events, setEvents] = useState(null);   // null = cargando
  const [eventId, setEventId] = useState(null);
  const [event, setEvent] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const go = (key) => { setSection(key); setMenuOpen(false); };

  // Los eventos ya vienen filtrados por rol desde el backend.
  useEffect(() => {
    api.get('/api/events')
      .then(list => {
        const arr = Array.isArray(list) ? list : [];
        setEvents(arr);
        // Si hay uno solo, se abre directo: el caso normal de una noche.
        if (arr.length === 1) setEventId(arr[0].id);
      })
      .catch(() => setEvents([]));
  }, []); // eslint-disable-line

  // Carga del evento elegido. Las pestañas esperan el evento completo (con lots,
  // metrics, etc.), no el resumen del listado.
  const loadEvent = () => {
    if (!eventId) { setEvent(null); return; }
    api.get(`/api/events/${eventId}`).then(setEvent).catch(() => setEvent(null));
  };
  useEffect(loadEvent, [eventId]); // eslint-disable-line

  const tabProps = { event, api, reload: loadEvent };

  return (
    <div className={styles.layout}>
      {menuOpen && <div className={styles.backdrop} onClick={() => setMenuOpen(false)} />}
      <aside className={`${styles.sidebar} ${menuOpen ? styles.sidebarOpen : ''}`}>
        <div className={styles.sidebarBrand}>
          <img src="/assets/tukipass-icon.png" alt="Tukipass" className={styles.brandMark} style={{ objectFit: 'cover' }} />
          <div>
            <div className={styles.brandName}>TUKIPRO</div>
            <div className={styles.brandRole}>ADMINISTRADOR</div>
          </div>
        </div>
        <nav className={styles.nav}>
          {SECTIONS.map(s => (
            <button
              key={s.key}
              className={`${styles.navItem} ${section === s.key ? styles.navActive : ''}`}
              onClick={() => go(s.key)}
              disabled={!eventId}
              style={!eventId ? { opacity: 0.45, cursor: 'default' } : undefined}
            >
              {s.label}
            </button>
          ))}
        </nav>
        {/* Volver a la lista cuando hay más de un evento */}
        {eventId && (events || []).length > 1 && (
          <button className={styles.navItem} style={{ marginTop: 'auto', opacity: 0.8 }} onClick={() => { setEventId(null); setMenuOpen(false); }}>
            ← Cambiar de evento
          </button>
        )}
        <button className={styles.logoutBtn} onClick={logout}>Cerrar sesión</button>
      </aside>

      <main className={styles.main}>
        <div className={styles.topBar}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
            <button className={styles.menuToggle} onClick={() => setMenuOpen(o => !o)} aria-label="Menú">☰</button>
            <div className={styles.pageTitle}>
              {eventId && event ? event.name : 'Mis eventos'}
            </div>
            {eventId && event?.date && (
              <span style={{ fontSize: 12, color: 'var(--ink-600)', whiteSpace: 'nowrap' }}>{fmtDate(event.date)}</span>
            )}
          </div>
          <div className={styles.userChip}>{user?.name}</div>
        </div>

        <div className={styles.content}>
          {/* Sin evento elegido: la lista para entrar a uno */}
          {!eventId && (
            <>
              {events === null && <Aviso>Cargando eventos…</Aviso>}
              {events?.length === 0 && (
                <Aviso>
                  Todavía no hay eventos asignados a tu cuenta.<br />
                  <span style={{ fontSize: 12 }}>Pedile al dueño del boliche que te agregue al evento.</span>
                </Aviso>
              )}
              {/* Separados en próximos y finalizados, con el mismo criterio que el
                  panel del dueño (isPast). Lo que se está por trabajar va primero. */}
              {events?.length > 0 && (() => {
                const proximos = events.filter(e => !isPast(e));
                const finalizados = events.filter(e => isPast(e));
                return (
                  <>
                    {proximos.length > 0 && (
                      <>
                        <div className="lb-eyebrow" style={{ marginBottom: 12 }}>Próximos</div>
                        <EventGrid list={proximos} onPick={setEventId} />
                      </>
                    )}
                    {finalizados.length > 0 && (
                      <>
                        <div className="lb-eyebrow" style={{ margin: proximos.length ? '28px 0 12px' : '0 0 12px', color: 'var(--ink-600)' }}>
                          Finalizados
                        </div>
                        <EventGrid list={finalizados} onPick={setEventId} apagado />
                      </>
                    )}
                  </>
                );
              })()}
            </>
          )}

          {/* Con evento elegido: la pestaña correspondiente */}
          {eventId && !event && <Aviso>Cargando el evento…</Aviso>}
          {eventId && event && (
            <>
              {section === 'ventas' && <TabVentas {...tabProps} />}
              {section === 'ingreso' && <TabIngreso {...tabProps} />}
              {section === 'cajas' && <TabCajas {...tabProps} />}
              {section === 'administracion' && <TabAdministracion {...tabProps} />}
            </>
          )}
        </div>
      </main>
    </div>
  );
}

// Grilla de eventos. Los finalizados se ven apagados: siguen siendo accesibles
// (hay que poder revisar la rendición de una noche pasada) pero no compiten con lo
// que está por venir.
function EventGrid({ list, onPick, apagado }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 14 }}>
      {list.map(ev => (
        <button key={ev.id} onClick={() => onPick(ev.id)} className="lb-card"
          style={{
            padding: '20px 22px', textAlign: 'left', cursor: 'pointer',
            border: '1px solid var(--border)', opacity: apagado ? 0.6 : 1,
          }}>
          <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 16, color: '#fff', textTransform: 'uppercase' }}>{ev.name}</div>
          <div style={{ fontSize: 12, color: 'var(--ink-600)', marginTop: 6 }}>{ev.date ? fmtDate(ev.date) : 'Sin fecha'}</div>
        </button>
      ))}
    </div>
  );
}

function Aviso({ children }) {
  return (
    <div style={{ padding: '70px 0', textAlign: 'center', color: 'var(--ink-600)', fontSize: 13, lineHeight: 1.7 }}>
      {children}
    </div>
  );
}
